import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import AuthLayout from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Forgot your password?"
      subtitle="Enter your email and we'll send you a secure reset link."
      footer={
        <Link to="/login" className="inline-flex items-center gap-1 text-blue-700 font-semibold hover:underline" data-testid="forgot-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to login
        </Link>
      }
    >
      {sent ? (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 text-center" data-testid="forgot-success">
          <CheckCircle2 className="h-10 w-10 text-blue-600 mx-auto" />
          <h3 className="font-heading text-lg font-bold text-slate-900 mt-3">Check your inbox</h3>
          <p className="text-sm text-slate-600 mt-2">
            If an account exists for <b>{email}</b>, you&apos;ll receive a password reset link valid for 1 hour.
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div>
            <Label htmlFor="fp-email">Email</Label>
            <Input
              id="fp-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="mt-1.5 h-11"
              data-testid="forgot-input-email"
              autoComplete="email"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="forgot-submit-btn"
            className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold"
          >
            <Mail className="h-4 w-4 mr-2" />
            {loading ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}
